import PropTypes from "prop-types";
import { memo, useContext, useMemo, useState } from "react";
import { useTransition, animated } from "react-spring";
import MoreDetails from "app/pages/.shared/MoreDetails.jsx";
import { FormattedMessage } from "react-intl";
import AirlineLogo from "app/pages/.shared/AirlineLogo";
import { BRANDS, TRANSPORTATION_TYPES } from "app/constants";
import {
	buildItineraryDetailData,
	buildItineraryPreviewData,
} from "app/pages/.shared/ItineraryUtils/ItineraryUtils";
import ItineraryView from "app/pages/.shared/ItineraryView";

import "./Itinerary.scss";
import { findAllRouteMismatchesWithTags } from "./ItineraryUtils/ItineraryUtils";
import Button, { BUTTON_VARIANTS } from "app/pages/.shared/form/Button";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import appGlobalsContext from "app/AppGlobalsContext";

const Itinerary = ({
	transportationType,
	inbound = {},
	outbound = {},
	disableDetails = false,
	displayRouteDetails = false,
	inboundSupplierClass,
	outboundSupplierClass,
}) => {
	const { brand } = useContext(appGlobalsContext);
	const [showRouteDetails, setShowRouteDetails] = useState(displayRouteDetails);
	const flightInboundPreviewData = useMemo(() => buildItineraryPreviewData(inbound), [inbound]);
	const flightInboundDetailData = useMemo(() => buildItineraryDetailData(inbound), [inbound]);
	const flightOutboundPreviewData = useMemo(() => buildItineraryPreviewData(outbound), [
		outbound,
	]);
	const flightOutboundDetailData = useMemo(() => buildItineraryDetailData(outbound), [outbound]);

	const isTrainType = transportationType === TRANSPORTATION_TYPES.TRAIN;
	const showDetailsLabel = showRouteDetails ? (
		<FormattedMessage
			id={
				isTrainType
					? "quotation.transport.route.expanded.label"
					: "quotation.flight.route.expanded.label"
			}
		/>
	) : (
		<FormattedMessage
			id={
				isTrainType
					? "quotation.transport.route.collapsed.label"
					: "quotation.flight.route.collapsed.label"
			}
		/>
	);

	const outboundItineraryLabel = isTrainType
		? "quotation.transport.outbound.title"
		: "quotation.flight.outbound.title";
	const inboundItineraryLabel = isTrainType
		? "quotation.transport.inbound.title"
		: "quotation.flight.inbound.title";

	const transitionsOutboundPreview = useTransition(!showRouteDetails, p => p, {
		from: { transform: "translate3d(0,-20px,0)" },
		enter: { transform: "translate3d(0,0px,0)" },
		leave: { transform: "translate3d(0,-20px,0)" },
		unique: true,
		reset: true,
		config: {
			duration: 250,
		},
	});

	const transitionsInboundPreview = useTransition(!showRouteDetails, p => p, {
		from: { transform: "translate3d(0,20px,0)" },
		enter: { transform: "translate3d(0,0px,0)" },
		leave: { transform: "translate3d(0,20px,0)" },
		unique: true,
		reset: true,
		config: {
			duration: 250,
		},
	});

	const transitionsOutboundDetail = useTransition(showRouteDetails, p => p, {
		from: { transform: "translate3d(0,100px,0)" },
		enter: { transform: "translate3d(0,0px,0)" },
		leave: { transform: "translate3d(0,100px,0)" },
		unique: true,
		reset: true,
		config: {
			duration: 250,
		},
	});

	const transitionsInboundDetail = useTransition(showRouteDetails, p => p, {
		from: { transform: "translate3d(0,-100px,0)" },
		enter: { transform: "translate3d(0,0px,0)" },
		leave: { transform: "translate3d(0,-100px,0)" },
		reset: true,
		unique: true,
		config: {
			duration: 250,
		},
	});

	findAllRouteMismatchesWithTags(flightOutboundDetailData, flightInboundDetailData);
	findAllRouteMismatchesWithTags(flightOutboundPreviewData, flightInboundPreviewData);

	return (
		<>
			{showRouteDetails ? (
				<>
					{transitionsOutboundDetail.map(
						({ item, key, props }) =>
							item && (
								<animated.div key={key} style={props}>
									<div className="itinerary">
										<div className="itinerary__route-detail">
											<MoreDetails expanded={showRouteDetails}>
												<div className="quotation-flights__itinerary-details">
													<ItineraryView
														label={outboundItineraryLabel}
														flightData={flightOutboundDetailData}
														cabin={outbound.cabin}
														showDetails
														{...outboundSupplierClass && {
															supplierClass: outboundSupplierClass,
														}}
													/>
												</div>
											</MoreDetails>
										</div>
									</div>
								</animated.div>
							)
					)}
					{transitionsInboundDetail.map(
						({ item, key, props }) =>
							item && (
								<animated.div key={key} style={props}>
									<div className="itinerary">
										<div className="itinerary__route-detail">
											<MoreDetails expanded={showRouteDetails}>
												<div className="quotation-flights__itinerary-details">
													<ItineraryView
														label={inboundItineraryLabel}
														flightData={flightInboundDetailData}
														cabin={inbound.cabin}
														showDetails
														{...inboundSupplierClass && {
															supplierClass: inboundSupplierClass,
														}}
													/>
												</div>
											</MoreDetails>
										</div>
									</div>
								</animated.div>
							)
					)}
				</>
			) : (
				<div className="itinerary">
					{transitionsOutboundPreview.map(
						({ item, key, props }) =>
							item && (
								<animated.div key={key} style={props}>
									<div className="itinerary__item itinerary__item--outbound ">
										<ItineraryView
											label={outboundItineraryLabel}
											flightData={flightOutboundPreviewData}
											{...outboundSupplierClass && {
												supplierClass: outboundSupplierClass,
											}}
										/>
									</div>
								</animated.div>
							)
					)}
					{transitionsInboundPreview.map(
						({ item, key, props }) =>
							item && (
								<animated.div key={key} style={props}>
									<div className="itinerary__item ">
										<ItineraryView
											label={inboundItineraryLabel}
											flightData={flightInboundPreviewData}
											{...inboundSupplierClass && {
												supplierClass: inboundSupplierClass,
											}}
										/>
									</div>
								</animated.div>
							)
					)}
				</div>
			)}

			{inbound?.airline?.code === "TO" &&
				outbound?.airline?.code === "TO" &&
				brand !== BRANDS.TO && (
					<div className="itinerary__airline-note">
						<div className="airline-note">
							<div className="airline-note__logo">
								<AirlineLogo airlineCode={inbound.airline.code} />
							</div>
							<Typography
								variant={TYPOGRAPHY_VARIANTS.SMALL}
								className="airline-note__text"
							>
								<FormattedMessage id="quotation.flights.transavia.note" />
							</Typography>
						</div>
					</div>
				)}

			{!disableDetails && (
				<div className="quotation-flights__itinerary-details-button">
					<Button
						variant={BUTTON_VARIANTS.BORDERLESS}
						onClick={() => setShowRouteDetails(!showRouteDetails)}
						label={showDetailsLabel}
					/>
				</div>
			)}
		</>
	);
};

Itinerary.propTypes = {
	transportationType: PropTypes.string,
	inbound: PropTypes.object,
	outbound: PropTypes.object,
	disableDetails: PropTypes.bool,
	displayRouteDetails: PropTypes.bool,
	inboundSupplierClass: PropTypes.string,
	outboundSupplierClass: PropTypes.string,
};

export default memo(Itinerary);
